/**
 * BiomeConfig - Default configuration and documentation for biome creation
 *
 * This file provides:
 * 1. DEFAULT_CONFIG - Base configuration values for all biomes
 * 2. Documentation for each configuration option
 * 3. Helper function to merge custom config with defaults
 *
 * When creating a new biome, extend BiomeRenderer and override
 * these values in your constructor to customize the biome's appearance.
 *
 * Example usage:
 *   import { DEFAULT_CONFIG, createBiomeConfig } from './BiomeConfig.js';
 *   const myConfig = createBiomeConfig({
 *       terrainColor: 0x4a5d23,
 *       rockProbability: 0.3,
 *   });
 */

/**
 * Default configuration for all biomes
 * These values are used when a biome doesn't specify its own
 */
export const DEFAULT_CONFIG = {
    // ═══════════════════════════════════════════════════════════════════════
    // ROAD CONFIGURATION
    // Controls how the road curves and flows through the biome
    //
    // ROAD GENERATION SYSTEM:
    // The road uses a hash-based noise system with prime-number intervals to
    // create truly non-repeating curves and hills. Multiple hash lookups at
    // incommensurate frequencies (prime numbers) are summed with different
    // weights to create organic, natural-feeling road paths with no
    // detectable patterns.
    //
    // Lateral (curves): 4 frequencies at intervals 3571, 1847, 743, 293
    // Vertical (hills): 3 frequencies at intervals 2903, 1279, 509
    //
    // The prime numbers ensure patterns never sync up (LCM is astronomical).
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Width of the road surface in world units
     * Wider roads feel more open, narrower roads feel more challenging
     * Typical range: 8-15
     */
    roadWidth: 10,

    /**
     * Length of each road chunk in world units
     * Affects how often chunks are recycled
     * Typical range: 30-100
     */
    chunkLength: 50,

    /**
     * Distance between CatmullRom spline control points
     * The hash-based road position is sampled at these intervals
     * Larger = smoother visual interpolation, smaller = tighter following of hash
     * Typical range: 100-300
     */
    pathSegmentLength: 150,

    /**
     * How many path segments to generate ahead of the camera
     * Higher values prevent "popping" but use more memory
     * Typical range: 80-150
     */
    pathLookAhead: 100,

    /**
     * Maximum horizontal deviation of the road from center (world units)
     * Controls how far left/right the road can curve
     * Higher = more dramatic winding, lower = straighter road
     * Typical range: 150-300
     */
    roadMaxLateralOffset: 250,

    /**
     * Maximum vertical deviation - hills and valleys (world units)
     * Controls how steep hills and deep valleys can be
     * Higher = more dramatic elevation changes
     * Typical range: 30-60
     */
    roadMaxVerticalOffset: 40,

    /**
     * Spline tension for road curves (0-1)
     * 0 = tighter following of control points
     * 1 = very smooth/loose curves with more overshoot
     * Typical range: 0.0-0.3 (low values recommended)
     */
    roadSplineTension: 0.0,

    // ═══════════════════════════════════════════════════════════════════════
    // ROAD MARKINGS CONFIGURATION
    // Controls center line dashes and edge markings
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Length of center line dashes in world units
     * US DOT standard: 10ft (~3m) - scaled to game units
     * Typical range: 2-5
     */
    dashLength: 3,

    /**
     * Gap between center line dashes in world units
     * US DOT standard: 30ft (~9m) - scaled to game units
     * Typical range: 6-12
     */
    dashGapLength: 9,

    /**
     * Width of center line dashes
     * Typical range: 0.2-0.5
     */
    dashWidth: 0.3,

    /**
     * Curvature threshold for no-passing zones (double yellow lines)
     * Lower = more sensitive, triggers on gentler curves
     * Higher = only very sharp curves trigger double lines
     * Typical range: 0.0002-0.002
     */
    noPassingCurvatureThreshold: 0.0002,

    /**
     * Slope threshold for no-passing zones (double yellow lines)
     * Higher = only steep hills trigger double lines
     * Typical range: 0.02-0.1
     */
    noPassingSlopeThreshold: 0.025,

    /**
     * How many segments to look ahead for upcoming curves/hills
     * Higher = no-passing zones start earlier before blind curves
     * Typical range: 8-16
     */
    noPassingLookAhead: 12,

    /**
     * Spacing between the two lines in double yellow marking
     * Typical range: 0.3-0.6
     */
    doubleLineSpacing: 0.5,

    /**
     * Width of each line in double yellow marking
     * Typical range: 0.1-0.15
     */
    doubleLineWidth: 0.12,

    // ═══════════════════════════════════════════════════════════════════════
    // ROAD SURFACE CONFIGURATION
    // Controls road color and appearance
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Base road surface color (dark asphalt)
     */
    roadColorBase: 0x333333,

    /**
     * Variant road surface color for gradient (lighter grey)
     */
    roadColorVariant: 0x666666,

    /**
     * Scale of road color gradient noise
     * Lower = larger color regions, Higher = more frequent changes
     * Typical range: 0.0001-0.001
     */
    roadColorGradientScale: 0.0003,

    /**
     * Strength of road color gradient (0-1)
     * 0 = no gradient (all base color), 1 = full blend to variant
     */
    roadColorGradientStrength: 1.0,

    // ═══════════════════════════════════════════════════════════════════════
    // ROAD TEXTURE CONFIGURATION
    // Procedural noise texture for tarmac/concrete look
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Enable procedural road texture
     */
    roadTextureEnabled: true,

    /**
     * Overall strength/opacity of the road texture (0-1)
     * 0 = no texture visible, 1 = full texture effect
     * Typical range: 0.05-0.2
     */
    roadTextureStrength: 0.01,

    /**
     * Scale of coarse aggregate/patches
     * Lower = larger patches, Higher = finer pattern
     * Typical range: 0.1-0.5
     */
    roadTextureScaleCoarse: 0.2,

    /**
     * Strength of coarse texture layer (0-1)
     * Typical range: 0.3-0.7
     */
    roadTextureCoarseStrength: 0.3,

    /**
     * Scale of fine grain detail
     * Lower = larger grain, Higher = finer grain
     * Typical range: 0.5-2.0
     */
    roadTextureScaleFine: 2,

    /**
     * Strength of fine texture layer (0-1)
     * Typical range: 0.3-0.7
     */
    roadTextureFineStrength: 0.5,

    // ═══════════════════════════════════════════════════════════════════════
    // TERRAIN CONFIGURATION
    // Controls the landscape surrounding the road
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Base terrain color (hex)
     * This gets tinted by the time-of-day palette
     */
    terrainColor: 0x5d4e6d,

    /**
     * Large-scale terrain noise amplitude
     * Creates the overall mountain/valley shapes
     * Typical range: 100-600
     */
    terrainAmplitudeLarge: 400,

    /**
     * Medium-scale terrain noise amplitude
     * Creates rolling hills
     * Typical range: 50-300
     */
    terrainAmplitudeMedium: 200,

    /**
     * Small-scale terrain noise amplitude
     * Creates bumps and undulations
     * Typical range: 20-100
     */
    terrainAmplitudeSmall: 40,

    /**
     * Fine detail terrain noise amplitude
     * Creates small surface irregularities
     * Typical range: 5-30
     */
    terrainAmplitudeFine: 10,

    /**
     * Overall terrain height multiplier
     * Use to scale all terrain heights uniformly
     * Typical range: 0.5-2.0
     */
    terrainHeightMultiplier: 1.0,

    /**
     * Y-level for water plane (-1 = no water)
     * Areas below this level will show water
     */
    waterLevel: -10,

    // ═══════════════════════════════════════════════════════════════════════
    // TERRAIN DISTANCE VARIATION
    // Controls how close mountains/terrain can get to the road
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Minimum flat zone as fraction of terrain width
     * At terrainWidth=2000, 0.01 = 20 units minimum flat zone
     * Lower = mountains can get closer to road
     * Typical range: 0.01-0.1
     */
    terrainFlatZoneMin: 0.01,

    /**
     * Maximum flat zone as fraction of terrain width
     * At terrainWidth=2000, 0.1 = 200 units maximum flat zone
     * Typical range: 0.05-0.15
     */
    terrainFlatZoneMax: 0.1,

    /**
     * Blend range as fraction of terrain width
     * Controls how gradually terrain rises from flat zone to full height
     * Typical range: 0.1-0.25
     */
    terrainBlendRange: 0.15,

    /**
     * Noise scale for terrain distance variation along road
     * Lower = larger regions of similar distance
     * Higher = more frequent distance changes
     * Typical range: 0.001-0.005
     */
    terrainDistanceNoiseScale: 0.002,

    /**
     * Width of terrain extending from road edge (each side)
     * Higher = more visible terrain, but ensure fog hides edges
     * Typical range: 2000-5000
     */
    terrainWidth: 500,

    /**
     * Number of mesh segments across terrain width (perpendicular to road)
     * Higher = more detail but more vertices
     * Typical range: 40-80
     */
    terrainSegmentsOut: 50,

    /**
     * Number of mesh segments along terrain length (parallel to road per chunk)
     * Higher = smoother curves following road, but more vertices
     * Typical range: 20-40
     */
    terrainSegmentsAlong: 30,

    // ═══════════════════════════════════════════════════════════════════════
    // ROADSIDE STRIP CONFIGURATION
    // High-resolution terrain strip between road edge and mountain terrain
    // Prevents gaps during road curves while keeping low-poly mountain aesthetic
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Width of the high-res roadside strip in world units
     * This strip follows road curves precisely
     * Typical range: 40-100
     */
    roadsideStripWidth: 60,

    /**
     * Number of segments perpendicular to road within the strip
     * Higher = smoother strip terrain
     * Typical range: 3-8
     */
    roadsideStripSegments: 4,

    /**
     * How far the strip extends under the road edge
     * Prevents gaps between road and terrain
     * Typical range: 0.1-0.3
     */
    roadsideStripOverlap: 0.15,

    /**
     * How far mountain terrain overlaps into the strip
     * Hides seams between strip and mountain meshes
     * Typical range: 3-10
     */
    mountainOverlapIntoStrip: 5,

    // ═══════════════════════════════════════════════════════════════════════
    // TERRAIN FEATURE SYSTEM
    // Controls variety in terrain shapes (peaks, plateaus, etc.)
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Enable the feature-based terrain system
     * When false, uses simple multi-octave noise
     */
    terrainFeaturesEnabled: true,

    /**
     * Scale of the feature map noise (determines feature region size)
     * Lower = larger feature regions, Higher = more varied
     * Typical range: 0.00005-0.0002
     */
    terrainFeatureScale: 0.00008,

    /**
     * Probability weights for each terrain feature type
     * Values are relative weights (will be normalized)
     * Set to 0 to disable a feature type for this biome
     */
    terrainFeatureWeights: {
        dunes: 1.0,       // Rolling hills/dunes (smooth)
        peaks: 0.8,       // Tall pointed mountains
        plateaus: 0.6,    // Flat-topped mesas
        spires: 0.3,      // Narrow tall formations
        cliffs: 0.4,      // Steep drop-offs
        valleys: 0.5      // Carved low areas
    },

    /**
     * How gradually features blend into each other
     * Higher = smoother transitions, Lower = more distinct regions
     * Typical range: 0.2-0.5
     */
    terrainFeatureBlend: 0.35,

    /**
     * Height multipliers for distance-based terrain layers
     * Index 0 = near road, higher indices = further from road
     * Creates depth with smaller features near, larger far
     */
    terrainLayerHeights: [0.3, 0.6, 1.0, 1.4],

    /**
     * Distance thresholds (as fraction of terrainWidth) for each layer
     * Must have same length as terrainLayerHeights
     */
    terrainLayerDistances: [0.1, 0.25, 0.5, 1.0],

    // ═══════════════════════════════════════════════════════════════════════
    // ROADSIDE OBJECTS CONFIGURATION
    // Controls rocks, shrubs, and other decorative objects near the road
    // ═══════════════════════════════════════════════════════════════════════

    /**
     * Whether to spawn roadside objects
     */
    enableRoadsideObjects: true,

    /**
     * Distance ahead of camera to spawn objects
     * Should be less than fog distance for smooth appearance
     * Typical range: 500-1000
     */
    roadsideObjectSpawnDistance: 700,

    /**
     * Object density (0-1)
     * 0 = very sparse, 1 = very dense
     * Typical range: 0.2-0.5
     */
    roadsideObjectDensity: 0.3,

    /**
     * Minimum distance from road center where objects can spawn
     * Prevents objects from appearing on the road
     * Typical range: 8-15
     */
    roadsideObjectMinSafeZone: 10,

    /**
     * Maximum distance from road where objects spawn
     * Higher = objects spread further from road
     * Typical range: 100-300
     */
    roadsideObjectDistributionWidth: 200,

    /**
     * Probability that an object is a rock (vs shrub)
     * 0 = all shrubs, 1 = all rocks
     * Typical range: 0.3-0.7
     */
    rockProbability: 0.6,

    /**
     * Rock size range (world units)
     */
    rockMinSize: 2,
    rockMaxSize: 7,

    /**
     * Shrub size range (world units)
     */
    shrubMinSize: 3,
    shrubMaxSize: 10,

    /**
     * Number of different shrub sprites to use
     * More variety = less repetitive appearance
     */
    shrubSpriteCount: 3,

    /**
     * Random scale variation for shrubs (0-1)
     * 0 = all same size, 0.5 = ±50% size variation
     */
    shrubScaleVariation: 0.3,

    /**
     * Random vertical stretch variation for shrubs (0-1)
     * Adds visual variety to shrub shapes
     */
    shrubStretchVariation: 0.3,

    /**
     * Fade-in duration for new objects (seconds)
     * Prevents "popping" when objects spawn
     */
    roadsideObjectFadeInDuration: 0.5,

    /**
     * Paths to shrub sprite images
     * Can be customized per biome for different vegetation
     */
    shrubSpritePaths: [
        'assets/sprites/shrub1.png',
        'assets/sprites/shrub2.png',
        'assets/sprites/shrub3.png',
        'assets/sprites/shrub4.png',
        'assets/sprites/shrub5.png'
    ]
};

/**
 * Biome registry entry structure
 * Used by BiomeManager to determine which biome to load
 */
export const BIOME_ENTRY_TEMPLATE = {
    minMiles: 0,        // Starting mileage for this biome
    maxMiles: 500,      // Ending mileage (next biome starts here)
    BiomeClass: null,   // Reference to the biome class
    name: 'Unnamed'     // Display name for debugging/UI
};

/**
 * Create a biome configuration by merging custom values with defaults
 *
 * @param {Object} customConfig - Custom configuration values
 * @returns {Object} Complete configuration with defaults for missing values
 *
 * @example
 * const mountainConfig = createBiomeConfig({
 *     terrainAmplitudeLarge: 800,
 *     roadMaxVerticalOffset: 50,
 *     roadMaxLateralOffset: 220,
 *     rockProbability: 0.8
 * });
 */
export function createBiomeConfig(customConfig = {}) {
    return {
        ...DEFAULT_CONFIG,
        ...customConfig
    };
}

/**
 * Validate a biome configuration
 * Logs warnings for values outside typical ranges
 *
 * @param {Object} config - Configuration to validate
 * @param {string} biomeName - Name of the biome (for logging)
 */
export function validateBiomeConfig(config, biomeName = 'Unknown') {
    const warnings = [];

    // Road validation
    if (config.roadWidth < 5 || config.roadWidth > 30) {
        warnings.push(`roadWidth (${config.roadWidth}) outside typical range 5-30`);
    }
    if (config.roadSplineTension < 0 || config.roadSplineTension > 1) {
        warnings.push(`roadSplineTension (${config.roadSplineTension}) should be 0-1`);
    }

    // Terrain validation
    if (config.terrainHeightMultiplier < 0) {
        warnings.push(`terrainHeightMultiplier should be positive`);
    }

    // Object validation
    if (config.roadsideObjectDensity < 0 || config.roadsideObjectDensity > 1) {
        warnings.push(`roadsideObjectDensity (${config.roadsideObjectDensity}) should be 0-1`);
    }
    if (config.rockProbability < 0 || config.rockProbability > 1) {
        warnings.push(`rockProbability (${config.rockProbability}) should be 0-1`);
    }

    if (warnings.length > 0) {
        console.warn(`[${biomeName}] Config warnings:`, warnings);
    }

    return warnings.length === 0;
}
